/*
 * logo-version-maker.js — ForTee Bundle Builder V2
 * @copyright Fortee Uniforme Inc.
 *
 * Client-side, dependency-free logo version generator. Produces garment-ready
 * logo versions entirely on <canvas> — no server, no Sharp, no per-image cost:
 *   - simple / monochrome logos       -> true white & black recolours
 *   - full-colour crests / photo logos -> kept in colour + a white contrast
 *                                         outline so they stay visible on dark
 *                                         garments (built from the cutout shape,
 *                                         so logo complexity does not matter)
 *   - embroidery + thumbnail derivations
 *
 * It also flags logos that need a human ("needs designer review") — both
 * automatically (poor cutout, too small, low-res, colour logo with no
 * transparency) and on demand from the UI.
 *
 * Public API (window.forteeLogoVersions):
 *   buildVersions(source, opts) -> Promise<{ source, analysis, versions[, urls] }>
 *   analyze(img) -> analysis
 *   pickVersion(analysis, garmentTone) -> version key
 *   recolor / outline / thumbnail / loadImage helpers
 *
 * Reuses the existing pipeline: pass { upload:true } to push each version to
 * Uploadcare via window.forteeShared.uploadToUploadcare (same CDN as every
 * other logo asset). Background removal stays where it already is — feed this a
 * logo whose background was already cleared by removeSolidBgAndUpload / the
 * paid fallback.
 */
(function () {
  'use strict';

  // Load an image for canvas work. Uploadcare + theme assets send CORS headers,
  // so request them cross-origin (otherwise the canvas taints and export fails).
  // Local object URLs (the test bench) ignore crossOrigin entirely.
  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () {
        // Retry without CORS so a preview still renders on hosts that omit the
        // header; export may taint, which the caller handles as an upload failure.
        var plain = new Image();
        plain.onload = function () { resolve(plain); };
        plain.onerror = function () { reject(new Error('Could not load image: ' + src)); };
        plain.src = src;
      };
      img.src = src;
    });
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  function dims(img) {
    return { w: img.naturalWidth || img.width, h: img.naturalHeight || img.height };
  }

  // Paint every opaque pixel a flat colour, preserving the alpha silhouette.
  // 'source-in' keeps the intersection of the fill with the existing alpha.
  function recolorCanvas(img, color) {
    var d = dims(img);
    var c = makeCanvas(d.w, d.h);
    var ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, d.w, d.h);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, d.w, d.h);
    ctx.globalCompositeOperation = 'source-over';
    return c;
  }

  // White (or any colour) sticker outline around the logo's silhouette. Uses ONLY
  // the alpha shape, so it works identically for a one-colour mark, a full-colour
  // crest, or a photo logo. `thickness` is a fraction of the logo's max dimension.
  function outlineCanvas(img, opts) {
    opts = opts || {};
    var color = opts.color || '#ffffff';
    var d = dims(img);
    var maxDim = Math.max(d.w, d.h);
    var t = opts.thicknessPx != null
      ? opts.thicknessPx
      : Math.max(2, Math.round(maxDim * (opts.thickness != null ? opts.thickness : 0.03)));
    var pad = t + 2;
    var c = makeCanvas(d.w + pad * 2, d.h + pad * 2);
    var ctx = c.getContext('2d');

    var sil = recolorCanvas(img, color); // solid silhouette in the outline colour

    if (opts.halo) {
      // Soft glow variant.
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = t * 1.6;
      for (var g = 0; g < 3; g++) ctx.drawImage(sil, pad, pad);
      ctx.restore();
    } else {
      // Hard sticker edge: stamp the silhouette around concentric rings of offsets
      // to dilate the shape by `t` pixels in every direction.
      var steps = 32;
      var rings = Math.max(1, Math.round(t / 1.5));
      for (var r = 1; r <= rings; r++) {
        var rad = (t * r) / rings;
        for (var s = 0; s < steps; s++) {
          var a = (s / steps) * Math.PI * 2;
          ctx.drawImage(sil, pad + Math.cos(a) * rad, pad + Math.sin(a) * rad);
        }
      }
    }

    // Original logo, full colour, on top of its white edge.
    ctx.drawImage(img, pad, pad, d.w, d.h);
    return c;
  }

  function thumbnailCanvas(img, size) {
    size = size || 256;
    var d = dims(img);
    var scale = Math.min(size / d.w, size / d.h, 1);
    var w = Math.round(d.w * scale), h = Math.round(d.h * scale);
    var c = makeCanvas(w, h);
    c.getContext('2d').drawImage(img, 0, 0, w, h);
    return c;
  }

  // Analyse colour complexity and flag logos that need a human. Runs on a small
  // downscaled copy for speed.
  function analyze(img) {
    var d = dims(img);
    var maxDim = Math.max(d.w, d.h);
    var s = Math.min(160, maxDim) / maxDim;
    var w = Math.max(1, Math.round(d.w * s)), h = Math.max(1, Math.round(d.h * s));
    var c = makeCanvas(w, h);
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      // Tainted canvas (cross-origin without CORS) — can't inspect pixels.
      return {
        colorClass: 'full', opaqueFraction: 1, avgSaturation: 1, distinctColors: 99,
        hasTransparency: false, needsReview: true, reviewReasons: ['could-not-analyze']
      };
    }

    var total = w * h, opaque = 0, satSum = 0, bins = {};
    for (var i = 0; i < total; i++) {
      if (data[i * 4 + 3] < 128) continue;
      opaque++;
      var r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      satSum += mx === 0 ? 0 : (mx - mn) / mx;
      var key = (r >> 5) + '.' + (g >> 5) + '.' + (b >> 5); // 3 bits / channel
      bins[key] = (bins[key] || 0) + 1;
    }

    var opaqueFraction = opaque / total;
    var avgSat = opaque ? satSum / opaque : 0;
    var populated = 0;
    for (var k in bins) { if (bins[k] / (opaque || 1) > 0.02) populated++; }

    var colorClass;
    if (avgSat < 0.12 || populated <= 1) colorClass = 'mono';
    else if (populated <= 3) colorClass = 'duotone';
    else colorClass = 'full';

    var reasons = [];
    if (maxDim < 200) reasons.push('low-resolution');
    if (opaqueFraction > 0.97) reasons.push('background-maybe-not-removed');
    if (opaqueFraction < 0.02) reasons.push('subject-too-small');
    var hasTransparency = opaqueFraction < 0.985;
    if (!hasTransparency && colorClass === 'full') reasons.push('full-colour-no-transparency');

    return {
      colorClass: colorClass,
      opaqueFraction: opaqueFraction,
      avgSaturation: avgSat,
      distinctColors: populated,
      hasTransparency: hasTransparency,
      needsReview: reasons.length > 0,
      reviewReasons: reasons
    };
  }

  // Which version to show for a garment tone (dark | light | medium).
  function pickVersion(analysis, garmentTone) {
    if (garmentTone === 'dark') {
      return (analysis && (analysis.colorClass === 'mono' || analysis.colorClass === 'duotone'))
        ? 'white'
        : 'outlineDark';
    }
    return 'original';
  }

  function toURL(canvas) { return canvas.toDataURL('image/png'); }

  async function buildVersions(source, opts) {
    opts = opts || {};
    var img = await loadImage(typeof source === 'string' ? source : source.url);
    var d = dims(img);
    var analysis = analyze(img);
    var versions = {};

    var base = makeCanvas(d.w, d.h);
    base.getContext('2d').drawImage(img, 0, 0, d.w, d.h);
    versions.original = toURL(base);

    // Recolours only make sense for simple logos; a full-colour crest would
    // collapse into a white blob, so we leave those null and use the outline.
    if (analysis.colorClass === 'mono' || analysis.colorClass === 'duotone') {
      versions.white = toURL(recolorCanvas(img, '#ffffff'));
      versions.black = toURL(recolorCanvas(img, '#111111'));
    } else {
      versions.white = null;
      versions.black = null;
    }

    // The main case: keep full colour + white outline for dark garments.
    versions.outlineDark = toURL(outlineCanvas(img, {
      color: opts.outlineColor || '#ffffff',
      thickness: opts.outlineThickness != null ? opts.outlineThickness : 0.03,
      halo: !!opts.halo
    }));

    versions.embroidery = (analysis.colorClass === 'mono')
      ? toURL(recolorCanvas(img, '#111111'))
      : versions.original;

    versions.thumbnail = toURL(thumbnailCanvas(img, opts.thumbSize || 256));

    var result = {
      source: { width: d.w, height: d.h },
      analysis: analysis,
      versions: versions
    };

    // Optional: reuse the existing Uploadcare uploader so downstream code gets
    // CDN URLs exactly like every other logo asset.
    if (opts.upload && window.forteeShared && typeof window.forteeShared.uploadToUploadcare === 'function') {
      var key = opts.pubkey || window.forteeShared._uploadcarePubkey;
      var urls = {};
      for (var name in versions) {
        if (!versions[name]) { urls[name] = null; continue; }
        try {
          urls[name] = await window.forteeShared.uploadToUploadcare(versions[name], key);
        } catch (e) {
          urls[name] = null;
        }
      }
      result.urls = urls;
    }

    return result;
  }

  window.forteeLogoVersions = {
    buildVersions: buildVersions,
    analyze: analyze,
    pickVersion: pickVersion,
    recolor: function (img, color) { return toURL(recolorCanvas(img, color)); },
    outline: function (img, o) { return toURL(outlineCanvas(img, o)); },
    thumbnail: function (img, size) { return toURL(thumbnailCanvas(img, size)); },
    loadImage: loadImage,
    VERSION: '0.1.0'
  };
})();
